import {
  ASTNode,
  DirectiveDefinitionNode,
  DocumentNode,
  EnumTypeDefinitionNode,
  EnumTypeExtensionNode,
  EnumValueDefinitionNode,
  FieldDefinitionNode,
  InputObjectTypeDefinitionNode,
  InputObjectTypeExtensionNode,
  InputValueDefinitionNode,
  InterfaceTypeDefinitionNode,
  InterfaceTypeExtensionNode,
  NamedTypeNode,
  ObjectTypeDefinitionNode,
  ObjectTypeExtensionNode,
  OperationTypeDefinitionNode,
  ScalarTypeDefinitionNode,
  ScalarTypeExtensionNode,
  SchemaDefinitionNode,
  SchemaExtensionNode,
  TypeNode,
  UnionTypeDefinitionNode,
  UnionTypeExtensionNode,
  visit,
} from 'graphql';
import { range } from 'lodash';

import { SDLHash } from 'src/app/graph/routes';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';

import { Coordinate } from '../../proposalLayout/diffHelpers/diffHelpers';

type LinkableDefinitionNode =
  | ScalarTypeDefinitionNode
  | ObjectTypeDefinitionNode
  | InterfaceTypeDefinitionNode
  | UnionTypeDefinitionNode
  | EnumTypeDefinitionNode
  | InputObjectTypeDefinitionNode
  | ScalarTypeExtensionNode
  | ObjectTypeExtensionNode
  | InterfaceTypeExtensionNode
  | UnionTypeExtensionNode
  | EnumTypeExtensionNode
  | InputObjectTypeExtensionNode
  | FieldDefinitionNode
  | InputValueDefinitionNode
  | EnumValueDefinitionNode
  | DirectiveDefinitionNode;

const LINKABLE_DEFINITION_TYPES = new Set<ASTNode['kind']>([
  'ScalarTypeDefinition',
  'ObjectTypeDefinition',
  'InterfaceTypeDefinition',
  'UnionTypeDefinition',
  'EnumTypeDefinition',
  'InputObjectTypeDefinition',
  'ScalarTypeExtension',
  'ObjectTypeExtension',
  'InterfaceTypeExtension',
  'UnionTypeExtension',
  'EnumTypeExtension',
  'InputObjectTypeExtension',
  'FieldDefinition',
  'InputValueDefinition',
  'EnumValueDefinition',
  'DirectiveDefinition',
]);

type LinkableSchemaDefinitionNode =
  | SchemaDefinitionNode
  | SchemaExtensionNode
  | OperationTypeDefinitionNode;

const SCHEMA_DEFINITION_TYPES = new Set<ASTNode['kind']>([
  'SchemaDefinition',
  'SchemaExtension',
  'OperationTypeDefinition',
]);

const VALID_TYPEMAP_TYPES = new Set<LinkableDefinitionNode['kind']>([
  'ScalarTypeDefinition',
  'ObjectTypeDefinition',
  'InterfaceTypeDefinition',
  'UnionTypeDefinition',
  'EnumTypeDefinition',
  'InputObjectTypeDefinition',
  'ScalarTypeExtension',
  'ObjectTypeExtension',
  'InterfaceTypeExtension',
  'UnionTypeExtension',
  'EnumTypeExtension',
  'InputObjectTypeExtension',
]);

function getLineToLinkOn(
  node:
    | LinkableDefinitionNode
    | SchemaDefinitionNode
    | SchemaExtensionNode
    | OperationTypeDefinitionNode,
) {
  let line = node.loc?.startToken.line;
  if ('description' in node && node.description?.loc?.endToken.line) {
    // TODO: remove this disabling rule for eslint v8 migration
    // eslint-disable-next-line no-unsafe-optional-chaining
    line = node.description?.loc?.endToken.line + 1;
  }
  return line;
}

function getDescriptionLinesToLinkOn(
  node:
    | LinkableDefinitionNode
    | SchemaDefinitionNode
    | SchemaExtensionNode
    | OperationTypeDefinitionNode,
) {
  let lines: number[] = [];
  if ('description' in node && node.description?.loc) {
    lines = range(
      node.description.loc.startToken.line,
      node.description.loc.endToken.line + 1,
    );
  }

  return lines;
}

function getNamedTypeFromTypeNode(node: TypeNode): NamedTypeNode {
  if (node.kind === 'NamedType') {
    return node;
  }
  return getNamedTypeFromTypeNode(node.type);
}

function getReturnTypeLineToLinkOn(
  node:
    | FieldDefinitionNode
    | InputValueDefinitionNode
    | OperationTypeDefinitionNode,
) {
  return getNamedTypeFromTypeNode(node.type).loc?.startToken.line;
}

function getRangeToLinkOn(
  node:
    | LinkableDefinitionNode
    | SchemaDefinitionNode
    | SchemaExtensionNode
    | OperationTypeDefinitionNode,
) {
  let startLine = node.loc?.startToken.line;
  const endLine = node.loc?.endToken.line;
  if ('description' in node && node.description?.loc?.startToken.line) {
    startLine = node.description.loc.startToken.line;
  }
  if (!startLine || !endLine) {
    return null;
  }

  return { startLine, endLine };
}

export function isLinkableNode(
  node: ASTNode | undefined | null,
): node is LinkableDefinitionNode {
  return !!node && LINKABLE_DEFINITION_TYPES.has(node.kind);
}

export function isSchemaDefinitionNode(
  node: ASTNode | undefined | null,
): node is LinkableSchemaDefinitionNode {
  return !!node && SCHEMA_DEFINITION_TYPES.has(node.kind);
}

export function isValidTypeMapNode(
  node: ASTNode | undefined | null,
): node is LinkableDefinitionNode {
  return isLinkableNode(node) && VALID_TYPEMAP_TYPES.has(node.kind);
}

export function mapLineNumsToTypes(typeDefs: DocumentNode) {
  const definitionTypeWithLocation: {
    lineNum: number | undefined;
    typeName: string;
  }[] = [];

  const lineNumbersToTypeNames: Record<number, string> = {};
  visit(typeDefs, {
    enter(node) {
      if (isValidTypeMapNode(node)) {
        const definitionLineToLinkOn = getLineToLinkOn(node);
        if (
          definitionLineToLinkOn &&
          !(definitionLineToLinkOn in Object.keys(definitionTypeWithLocation))
        ) {
          definitionTypeWithLocation.push({
            lineNum: definitionLineToLinkOn,
            typeName: node.name.value,
          });
        }
        const definitionRangeToLinkOn = getRangeToLinkOn(node);
        if (definitionRangeToLinkOn) {
          for (
            let i: number = definitionRangeToLinkOn.startLine;
            i <= definitionRangeToLinkOn.endLine;
            i++
          ) {
            lineNumbersToTypeNames[i] = node.name.value;
          }
        }
      }
    },
  });
  return { lineNumbersToTypeNames, definitionTypeWithLocation };
}

export function mapLineNumsToCoordinates({
  typeDefs,
  includeDescriptionLines,
  includeReturnTypes,
}: {
  typeDefs: DocumentNode;
  includeDescriptionLines?: boolean;
  includeReturnTypes?: boolean;
}) {
  // map line number to schema coordinate string
  const lineNumbersToCoordinates: Record<number, string[]> = {};

  visit(typeDefs, {
    enter(node) {
      if (node.kind === 'SchemaDefinition' || node.kind === 'SchemaExtension') {
        const lineToLinkOn = getLineToLinkOn(node);

        if (lineToLinkOn) {
          getOrSetRecord(lineToLinkOn, [], lineNumbersToCoordinates).push(
            '$schema',
          );
        }

        node.operationTypes?.forEach((operationTypeNode) => {
          const operationTypeLineToLinkOn = getLineToLinkOn(operationTypeNode);

          if (operationTypeLineToLinkOn) {
            getOrSetRecord(
              operationTypeLineToLinkOn,
              [],
              lineNumbersToCoordinates,
            ).push(`$schema.${operationTypeNode.operation}`);
          }
        });
      } else if (isLinkableNode(node)) {
        const lineToLinkOn = getLineToLinkOn(node);
        const descriptionLinesToLinkOn = includeDescriptionLines
          ? getDescriptionLinesToLinkOn(node)
          : [];
        const returnTypeLineToLinkOn =
          includeReturnTypes && 'type' in node
            ? getReturnTypeLineToLinkOn(node)
            : undefined;

        if (lineToLinkOn) {
          const hash = getHashParamsFromLineNumber(typeDefs, lineToLinkOn);

          if (!hash) return;

          const coordinate = getCoordinateFromSDLHash(hash);

          descriptionLinesToLinkOn.forEach((lineNumber) => {
            getOrSetRecord(lineNumber, [], lineNumbersToCoordinates).push(
              `${coordinate}~description`,
            );
          });
          getOrSetRecord(lineToLinkOn, [], lineNumbersToCoordinates).push(
            coordinate,
          );
          if (returnTypeLineToLinkOn) {
            getOrSetRecord(
              returnTypeLineToLinkOn,
              [],
              lineNumbersToCoordinates,
            ).push(`${coordinate}~returnType`);
          }
        }
      }
    },
  });
  return lineNumbersToCoordinates;
}

export function getAllSchemaCoordinates(typeDefs: DocumentNode) {
  const coordinates: string[] = [];

  visit(typeDefs, {
    enter(node) {
      if (node.kind === 'SchemaDefinition' || node.kind === 'SchemaExtension') {
        coordinates.push('$schema');

        node.operationTypes?.forEach((operationTypeNode) => {
          coordinates.push(`$schema.${operationTypeNode.operation}`);
        });
      } else if (isLinkableNode(node)) {
        const lineToLinkOn = getLineToLinkOn(node);
        if (lineToLinkOn) {
          const hash = getHashParamsFromLineNumber(typeDefs, lineToLinkOn);

          if (!hash) return;
          coordinates.push(getCoordinateFromSDLHash(hash));
        }
      }
    },
  });
  return coordinates;
}

export function getLinkableLineNumbers(
  typeDefs: DocumentNode,
): Record<number, string> {
  // map line number to kind of graphql definition (type, field, argument)
  const definitionTypeForNumber: Record<number, string> = {};

  visit(typeDefs, {
    enter(node) {
      if (isLinkableNode(node)) {
        const definitionLineToLinkOn = getLineToLinkOn(node);
        if (
          definitionLineToLinkOn &&
          !(definitionLineToLinkOn in definitionTypeForNumber)
        ) {
          definitionTypeForNumber[definitionLineToLinkOn] =
            node.kind === 'FieldDefinition'
              ? 'field'
              : node.kind === 'EnumValueDefinition'
                ? 'value'
                : node.kind === 'InputValueDefinition'
                  ? 'argument'
                  : node.kind === 'DirectiveDefinition'
                    ? 'directive'
                    : 'type';
        }
      }
    },
  });
  return definitionTypeForNumber;
}

export function getEditorLineNumber(
  typeDefs: DocumentNode,
  graphqlTypeName?: string,
  fieldName?: string,
  argumentName?: string,
  directiveName?: string,
  isSchemaDefinition?: boolean,
  isDescription?: boolean,
  isReturnType?: boolean,
  isSchemaExtension?: boolean,
) {
  // In addition to checking the schema extension, we should do a pass through
  // to see if any root types are defined on the extension. If not, we should check the schema definition.
  // We know we have a root type if schemaDefinition is true and we have a field name.
  const isRootTypeDefinition = isSchemaDefinition && fieldName;
  if (isSchemaExtension || isRootTypeDefinition) {
    const schemaExtensionNode = typeDefs.definitions.find(
      (definition) => definition.kind === 'SchemaExtension',
    );
    if (schemaExtensionNode && isSchemaDefinitionNode(schemaExtensionNode)) {
      if (fieldName) {
        const schemaExtensionRootTypeNode =
          'operationTypes' in schemaExtensionNode &&
          schemaExtensionNode.operationTypes?.find(
            (operationType) => operationType.operation === fieldName,
          );
        if (
          schemaExtensionRootTypeNode &&
          isSchemaDefinitionNode(schemaExtensionRootTypeNode)
        ) {
          if (isReturnType) {
            return getReturnTypeLineToLinkOn(schemaExtensionRootTypeNode);
          }

          return getLineToLinkOn(schemaExtensionRootTypeNode);
        }
      }
      if (!isRootTypeDefinition) {
        return getLineToLinkOn(schemaExtensionNode);
      }
    }
  }

  if (isSchemaDefinition) {
    const schemaDefinitionNode = typeDefs.definitions.find(
      (definition) => definition.kind === 'SchemaDefinition',
    );
    if (schemaDefinitionNode && isSchemaDefinitionNode(schemaDefinitionNode)) {
      if (fieldName) {
        const schemaDefinitionRootTypeNode =
          'operationTypes' in schemaDefinitionNode &&
          schemaDefinitionNode.operationTypes?.find(
            (operationType) => operationType.operation === fieldName,
          );
        if (
          schemaDefinitionRootTypeNode &&
          isSchemaDefinitionNode(schemaDefinitionRootTypeNode)
        ) {
          if (isReturnType) {
            return getReturnTypeLineToLinkOn(schemaDefinitionRootTypeNode);
          }

          return getLineToLinkOn(schemaDefinitionRootTypeNode);
        }
      }
      if (isDescription) {
        return getDescriptionLinesToLinkOn(schemaDefinitionNode)[0];
      }

      return getLineToLinkOn(schemaDefinitionNode);
    }
  }

  // to look up an argument, you must have a type name & field name,
  // to look up a field name you must have a typename
  // if a directiveName exists, nothing else is paid attention to
  const findNode = (kind: string, name: string) =>
    typeDefs.definitions.find(
      (definition) =>
        'name' in definition &&
        definition.name?.value === name &&
        definition.kind.includes(kind),
    );

  if (directiveName) {
    const directiveNode = findNode('DirectiveDefinition', directiveName);
    if (directiveNode && isLinkableNode(directiveNode)) {
      if (argumentName) {
        const argumentNode =
          'arguments' in directiveNode &&
          directiveNode.arguments?.find(
            (argument) => argument.name.value === argumentName,
          );
        if (argumentNode && isLinkableNode(argumentNode)) {
          if (isDescription) {
            return getDescriptionLinesToLinkOn(argumentNode)[0];
          }
          return getLineToLinkOn(argumentNode);
        }
      }
      if (isDescription) {
        return getDescriptionLinesToLinkOn(directiveNode)[0];
      }
      return getLineToLinkOn(directiveNode);
    }
  }

  if (graphqlTypeName) {
    const typeNode = findNode('Type', graphqlTypeName);
    if (typeNode && isLinkableNode(typeNode)) {
      if (fieldName) {
        const fieldNode =
          'fields' in typeNode &&
          typeNode.fields?.[
            typeNode.fields?.findIndex(
              (
                fieldDefinition: InputValueDefinitionNode | FieldDefinitionNode,
              ) => fieldDefinition.name.value === fieldName,
            )
          ];
        if (fieldNode && isLinkableNode(fieldNode)) {
          if (argumentName) {
            const argumentNode =
              'arguments' in fieldNode &&
              fieldNode.arguments?.find(
                (argument) => argument.name.value === argumentName,
              );
            if (argumentNode && isLinkableNode(argumentNode)) {
              if (isDescription) {
                return getDescriptionLinesToLinkOn(argumentNode)[0];
              }
              return getLineToLinkOn(argumentNode);
            }
          }
          if (isReturnType) {
            return getReturnTypeLineToLinkOn(fieldNode);
          }

          if (isDescription) {
            return getDescriptionLinesToLinkOn(fieldNode)[0];
          }
          return getLineToLinkOn(fieldNode);
        }

        const enumValueNode =
          'values' in typeNode &&
          typeNode.values?.find(
            (enumValue) => enumValue.name.value === fieldName,
          );
        if (enumValueNode && isLinkableNode(enumValueNode)) {
          if (isDescription) {
            return getDescriptionLinesToLinkOn(enumValueNode)[0];
          }
          return getLineToLinkOn(enumValueNode);
        }
      }
      if (isDescription) {
        return getDescriptionLinesToLinkOn(typeNode)[0];
      }
      return getLineToLinkOn(typeNode);
    }
  }

  return undefined;
}

export function getEditorLineNumberFromParsedCoordinate(
  typeDefs: DocumentNode,
  coordinate: Coordinate,
  isDescription?: boolean,
  isReturnType?: boolean,
) {
  if ('schemaDefinition' in coordinate && 'schemaExtension' in coordinate) {
    return getEditorLineNumber(
      typeDefs,
      undefined,
      coordinate.field,
      undefined,
      undefined,
      coordinate.schemaDefinition,
      isDescription,
      isReturnType,
      coordinate.schemaExtension,
    );
  } else if ('directive' in coordinate) {
    return getEditorLineNumber(
      typeDefs,
      undefined,
      undefined,
      coordinate.arg,
      coordinate.directive,
      undefined,
      isDescription,
    );
  } else if ('type' in coordinate) {
    return getEditorLineNumber(
      typeDefs,
      coordinate.type,
      coordinate.field,
      coordinate.arg,
      undefined,
      undefined,
      isDescription,
      isReturnType,
    );
  }
}

/**
 *
 * NOTICE: in the future if we need the map of all type defs to line numbers,
 * this can easily be modified to do that
 * @param typeDefs
 * @param lineNumber
 * @returns
 */
export function getHashParamsFromLineNumber(
  typeDefs: DocumentNode,
  lineNumber: number | undefined,
): SDLHash | undefined {
  let hashParams: SDLHash | undefined;
  typeDefs.definitions.some((typeDefinition) => {
    if (
      isLinkableNode(typeDefinition) &&
      getLineToLinkOn(typeDefinition) === lineNumber
    ) {
      hashParams =
        typeDefinition.kind === 'DirectiveDefinition'
          ? { directiveName: typeDefinition.name.value }
          : {
              graphqlTypeName: typeDefinition.name.value,
            };

      return true;
    }

    if ('fields' in typeDefinition && typeDefinition.fields) {
      typeDefinition.fields.some(
        (fieldDefinition: FieldDefinitionNode | InputValueDefinitionNode) => {
          if (getLineToLinkOn(fieldDefinition) === lineNumber) {
            hashParams = {
              graphqlTypeName: typeDefinition.name.value,
              fieldName: fieldDefinition.name.value,
            };
            return true;
          }

          if ('arguments' in fieldDefinition && fieldDefinition.arguments) {
            fieldDefinition.arguments.some((argumentDefinition) => {
              if (getLineToLinkOn(argumentDefinition) === lineNumber) {
                hashParams = {
                  graphqlTypeName: typeDefinition.name.value,
                  fieldName: fieldDefinition.name.value,
                  argumentName: argumentDefinition.name.value,
                };
                return true;
              }
              return false;
            });
          }
          return false;
        },
      );
    }
    if (
      typeDefinition.kind === 'DirectiveDefinition' &&
      'arguments' in typeDefinition &&
      typeDefinition.arguments
    ) {
      typeDefinition.arguments.some((argumentDefinition) => {
        if (getLineToLinkOn(argumentDefinition) === lineNumber) {
          hashParams = {
            directiveName: typeDefinition.name.value,
            argumentName: argumentDefinition.name.value,
          };
          return true;
        }
        return false;
      });
    }

    if ('values' in typeDefinition && typeDefinition.values) {
      typeDefinition.values.some((valueDefinition) => {
        if (getLineToLinkOn(valueDefinition) === lineNumber) {
          hashParams = {
            graphqlTypeName: typeDefinition.name.value,
            fieldName: valueDefinition.name.value,
          };
          return true;
        }
        return false;
      });
    }
    return false;
  });
  return hashParams;
}

function getCoordinateFromSDLHash(hash: SDLHash): string {
  if (hash.directiveName) {
    return hash.argumentName
      ? `@${hash.directiveName}(${hash.argumentName}:)`
      : `@${hash.directiveName}`;
  }
  return hash.argumentName
    ? `${hash.graphqlTypeName}.${hash.fieldName}(${hash.argumentName}:)`
    : hash.fieldName
      ? `${hash.graphqlTypeName}.${hash.fieldName}`
      : `${hash.graphqlTypeName}`;
}
